import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type RefObject,
} from 'react';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import { useToast } from '../../../toast/useToast';
import {
  collectFilesFromDataTransfer,
  hasFileDrag,
} from '../collectUploadFiles';
import { useUploadKnowledgeDocumentMutation } from '../mutations/useKnowledgeDocumentMutations';
import type { DocumentListOptions } from '../../../types/knowledge';

type UploadProgress = {
  current: number;
  total: number;
};

type UseDocumentUploadHandlersOptions = {
  databaseId: string;
  canManageDocuments: boolean;
  listOptions: DocumentListOptions;
};

export function useDocumentUploadHandlers({
  databaseId,
  canManageDocuments,
  listOptions,
}: UseDocumentUploadHandlersOptions) {
  const { translate } = useI18n();
  const { showToast, dismissToast } = useToast();
  const uploadMutation = useUploadKnowledgeDocumentMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const uploadWarningToastIdRef = useRef<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(
    null,
  );
  const [isUploadDragOver, setIsUploadDragOver] = useState(false);

  const isUploading = uploadProgress != null;

  const clearUploadWarningToast = useCallback(() => {
    const toastId = uploadWarningToastIdRef.current;
    if (!toastId) {
      return;
    }

    dismissToast(toastId);
    uploadWarningToastIdRef.current = null;
  }, [dismissToast]);

  const uploadFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0 || isUploading) {
        return;
      }

      void (async () => {
        uploadWarningToastIdRef.current =
          showToast('info', {
            title: translate('knowledge.uploadInProgressTitle'),
            description: translate('knowledge.uploadInProgressDescription'),
            durationMs: null,
          }) ?? null;

        setUploadProgress({ current: 1, total: files.length });
        let successCount = 0;
        let lastSuccessFileName = '';

        for (let index = 0; index < files.length; index += 1) {
          const file = files[index];
          if (!file) {
            continue;
          }

          setUploadProgress({ current: index + 1, total: files.length });

          const uploaded = await new Promise<boolean>((resolve) => {
            uploadMutation.mutate(
              {
                databaseId,
                file,
                listOptions,
              },
              {
                onSuccess: () => resolve(true),
                onError: (error) => {
                  void toastMutationError(
                    error,
                    translate('knowledge.uploadErrorTitle'),
                    translate('knowledge.uploadErrorDescription', {
                      fileName: file.name,
                    }),
                  );
                  resolve(false);
                },
              },
            );
          });

          if (uploaded) {
            successCount += 1;
            lastSuccessFileName = file.name;
          }
        }

        clearUploadWarningToast();
        setUploadProgress(null);

        if (successCount === 1) {
          toast.success({
            title: translate('knowledge.uploadSuccessTitle'),
            description: translate('knowledge.uploadSuccessDescription', {
              fileName: lastSuccessFileName,
            }),
          });
        } else if (successCount > 1) {
          toast.success({
            title: translate('knowledge.uploadBatchSuccessTitle'),
            description: translate('knowledge.uploadBatchSuccessDescription', {
              count: String(successCount),
            }),
          });
        }
      })();
    },
    [
      clearUploadWarningToast,
      databaseId,
      isUploading,
      listOptions,
      showToast,
      translate,
      uploadMutation,
    ],
  );

  const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    uploadFiles(files);
  };

  const handleFolderUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    uploadFiles(files);
  };

  const resetUploadDragState = useCallback(() => {
    dragDepthRef.current = 0;
    setIsUploadDragOver(false);
  }, []);

  const handleUploadDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (
        !canManageDocuments ||
        isUploading ||
        !hasFileDrag(event.dataTransfer)
      ) {
        return;
      }

      event.preventDefault();
      dragDepthRef.current += 1;
      setIsUploadDragOver(true);
    },
    [canManageDocuments, isUploading],
  );

  const handleUploadDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (!hasFileDrag(event.dataTransfer)) {
      return;
    }

    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsUploadDragOver(false);
    }
  }, []);

  const handleUploadDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (
        !canManageDocuments ||
        isUploading ||
        !hasFileDrag(event.dataTransfer)
      ) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    [canManageDocuments, isUploading],
  );

  const handleUploadDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      resetUploadDragState();

      if (
        !canManageDocuments ||
        isUploading ||
        !hasFileDrag(event.dataTransfer)
      ) {
        return;
      }

      void collectFilesFromDataTransfer(event.dataTransfer).then(uploadFiles);
    },
    [canManageDocuments, isUploading, resetUploadDragState, uploadFiles],
  );

  const openFilePicker = useCallback(() => {
    if (isUploading) {
      return;
    }

    fileInputRef.current?.click();
  }, [isUploading]);

  const openFolderPicker = useCallback(() => {
    if (isUploading) {
      return;
    }

    folderInputRef.current?.click();
  }, [isUploading]);

  return {
    fileInputRef: fileInputRef as RefObject<HTMLInputElement>,
    folderInputRef: folderInputRef as RefObject<HTMLInputElement>,
    handleUpload,
    handleFolderUpload,
    uploadFiles,
    isUploading,
    uploadProgress,
    isUploadDragOver,
    handleUploadDragEnter,
    handleUploadDragLeave,
    handleUploadDragOver,
    handleUploadDrop,
    openFilePicker,
    openFolderPicker,
  };
}
